import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  Stack,
  Button,
  Grid,
  Box,
  FormControl,
  Select,
  OutlinedInput,
  MenuItem,
  InputLabel,
  TextField,
  List,
  Tab,
  Tabs,
  Container,
  Chip,
  Badge,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  FileUpload as ImportIcon,
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  FormatListBulleted as ListIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import SiteLists from "../../../../components/SiteList";
import { Firestore } from "../../../../components/FirebaseAuth/firebase";
import {
  LOCAL_STORAGE_CURRENT_ACCOUNT,
  LOCAL_STORAGE_CURRENT_ORG,
  getLocalStorage,
} from "../../../../utils/localStorage";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { PageHeader, SectionHeader } from "../../../../components/Common";
import { StyledBarChart, StatsCard } from "../../../../components/Charts";
import { CardWithHeader } from "../../../../components/Common";
import { EmptyState } from "../../../../components/Common";
import AIAssistant from "../../../../components/AIAssistant";
import { useBreadcrumbs } from "../../../../hooks/useBreadcrumbs";
import AggregatedTrafficChart from "../../../../components/AggregatedTrafficChart/AggregatedTrafficChart";

/**
 * Enhanced and redesigned SiteList component
 */
const SiteList = () => {
  const title = "Sites";
  const isDataFetchedRef = useRef(false);
  const history = useNavigate();
  const [accountTraffic, setAccountTraffic] = useState(0);
  const currentAccount = getLocalStorage(LOCAL_STORAGE_CURRENT_ACCOUNT);
  const currentOrg = getLocalStorage(LOCAL_STORAGE_CURRENT_ORG);

  // State management
  const [sitesList, setSitesList] = useState({ data: [], isLoading: true });
  const [siteTypeSummary, setSiteTypeSummary] = useState([]);
  const [selectedSiteType, setSelectedSiteType] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("type") || "";
  });
  const [topSites, setTopSites] = useState([]);
  const [topSitesLoading, setTopSitesLoading] = useState(false);
  const [filteredSites, setFilteredSites] = useState([]);
  const [search, setSearch] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("search") || "";
  });
  const [siteData, setSiteData] = useState({
    trafficTrends: [],
    topEngagingSites: [],
    totalSites: 0,
    activeSites: 0,
    issueCount: 0,
  });
  const [tabValue, setTabValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return parseInt(params.get("tab")) || 0;
  });

  // Colors for charts
  const COLORS = [
    "#1976d2",
    "#ff9800",
    "#4caf50",
    "#f44336",
    "#9c27b0",
    "#00bcd4",
  ];

  /** 🔹 Fetch CMS Summary for the Account */
  const getSiteTypeSummary = useCallback(async () => {
    if (!currentAccount?.id) return;

    try {
      // First try to get CMS summary
      let summaryRef = collection(
        Firestore,
        "accounts",
        currentAccount.id,
        "cmsSummary"
      );
      let summaryData = await getDocs(summaryRef);

      // If no CMS summary exists, fall back to siteTypeSummary
      if (summaryData.empty) {
        summaryRef = collection(
          Firestore,
          "accounts",
          currentAccount.id,
          "siteTypeSummary"
        );
        summaryData = await getDocs(summaryRef);
      }

      let summaryArray = [];
      summaryData.forEach((doc) => {
        summaryArray.push({ type: doc.id, count: doc.data().count });
      });

      setSiteTypeSummary(summaryArray);
    } catch (error) {
      console.error("Error fetching CMS summary:", error);
      setSiteTypeSummary([]);
    } finally {
    }
  }, [currentAccount]);

  /** 🔹 Fetch Sites List */
  const fetchSitesList = useCallback(() => {
    try {
      const sitesCollectionsRef = collection(
        Firestore,
        `accounts/${currentAccount.id}/sites`
      );
      getDocs(sitesCollectionsRef).then((querySnapshot) => {
        const sitesArr = [];
        querySnapshot.forEach((doc) => {
          const siteData = doc.data();
          if (!siteData.status || siteData.status !== "deleted") {
            sitesArr.push({ ...siteData, id: doc.id });
          }
        });

        setSitesList({ data: sitesArr, isLoading: false });

        // Set some metrics for stats cards
        setSiteData((prev) => ({
          ...prev,
          totalSites: sitesArr.length,
          activeSites: sitesArr.filter((site) => site.status === "active")
            .length,
          // issueCount: Math.floor(Math.random() * 5) // Mock data, replace with real metrics
        }));
      });
    } catch (ex) {
      console.log("Error fetching sites:", ex);
      setSitesList({ data: [], isLoading: false });
    }
  }, [currentAccount.id]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    // Update URL with tab value
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("tab", newValue);
    window.history.replaceState(null, "", `?${searchParams.toString()}`);

    setTabValue(newValue);
  };

  const fetchAccountTraffic = async (accountId) => {
    try {
      const accountRef = doc(Firestore, `/accounts/${accountId}`);
      const accountData = (await getDoc(accountRef)).data();
      const date = new Date();
      const month = date.getMonth() + 1;
      const dateKey = `${
        month < 10 ? "0" + month : month
      }-${date.getFullYear()}`;
      if (
        accountData?.metrics[dateKey]?.["traffic"]?.["google"]?.["organic"]?.[
          "etv"
        ]
      ) {
        setAccountTraffic(
          new Intl.NumberFormat("en-US", {
            notation: "compact",
            maximumFractionDigits: 0,
          }).format(
            Math.round(
              accountData?.metrics?.[dateKey]?.["traffic"]?.["google"]?.[
                "organic"
              ]?.["etv"] ?? 0
            )
          )
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getTopSites = useCallback(async () => {
    if (!currentAccount?.id) return;

    try {
      setTopSitesLoading(true);
      const date = new Date();
      const month = date.getMonth() + 1;
      const dateKey = `${
        month < 10 ? "0" + month : month
      }-${date.getFullYear()}`;

      const accountRef = doc(Firestore, "accounts", currentAccount.id);
      const accountData = (await getDoc(accountRef)).data();

      if (accountData?.metrics?.[dateKey]?.topSites) {
        setTopSites(
          accountData.metrics[dateKey].topSites.filter(
            (site) => site.traffic > 0
          )
        );
      } else {
        setTopSites([]);
      }
    } catch (error) {
      console.error("Error fetching top sites:", error);
      setTopSites([]);
    } finally {
      setTopSitesLoading(false);
    }
  }, [currentAccount?.id]);

  useEffect(() => {
    if (isDataFetchedRef.current) return;

    isDataFetchedRef.current = true;

    fetchSitesList();
    getSiteTypeSummary();
    getTopSites();
  }, [fetchSitesList, getSiteTypeSummary, getTopSites]);

  // Use breadcrumb service for navigation
  useBreadcrumbs({
    organizationId: currentOrg?.id,
    accountId: currentAccount?.id,
    collectionName: currentAccount?.name,
    accountName: currentAccount?.name
  });

  useEffect(() => {
    let tempList = sitesList.data;
    if (sitesList.data.length === 0) return;

    if (selectedSiteType && selectedSiteType !== "All") {
      if (selectedSiteType === "Unknown") {
        const allSiteTypes = siteTypeSummary
          .filter((siteType) => siteType.type !== "Unknown")
          .map((siteType) => siteType.type);

        tempList = tempList.filter(
          (site) => !allSiteTypes.includes(site.cms || site.siteType)
        );
      } else {
        tempList = tempList.filter(
          (site) => (site.cms || site.siteType) === selectedSiteType
        );
      }
    }

    if (search.trim()) {
      tempList = tempList.filter((site) =>
        site.siteName.toLowerCase().includes(search.trim().toLowerCase())
      );
    }

    setFilteredSites(tempList);
  }, [sitesList.data, selectedSiteType, search, siteTypeSummary]);

  const handleCreate = useCallback(
    (isExternal) => {
      history(`/account/${currentAccount.id}/sites/create`, {
        state: { isExternal },
      });
    },
    [history, currentAccount.id]
  );

  useEffect(() => {
    fetchAccountTraffic(currentAccount.id);
  }, [currentAccount.id]);

  const handleImportSites = useCallback(() => {
    history(`/account/${currentAccount.id}/sites/import`);
  }, [history, currentAccount.id]);

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      {/* Page Header */}
      <PageHeader
        title={`${currentAccount.name} Sites`}
        subtitle="Manage and monitor all your sites in one place"
        endAction={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={handleImportSites}
              sx={{ borderRadius: "8px" }}
            >
              Import
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleCreate(false)}
              sx={{ borderRadius: "8px" }}
            >
              Create Site
            </Button>
          </Stack>
        }
      />

      {/* Dashboard Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            value={siteData.totalSites}
            label="Total Sites"
            icon={<DashboardIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            value={siteData.activeSites}
            label="Active Sites"
            icon={<DashboardIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            value={accountTraffic}
            label="Estimated Account Traffic for 30 days"
            subtitle="This is the estimated traffic for next 30 days, actual traffic may vary"
            icon={<DashboardIcon />}
            color="warning"
          />
        </Grid>
        {/* <Grid item xs={12} sm={6} md={4}>
          <StatsCard
            value={siteData.issueCount}
            label="Sites With Issues"
            icon={<DashboardIcon />}
            color="warning"
            trend={siteData.issueCount > 0 ? "up" : "down"}
            trendValue={siteData.issueCount > 0 ? "+" + siteData.issueCount : "0"}
          />
        </Grid> */}
      </Grid>

      {/* AI Assistant Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <AIAssistant
            contextType="account"
            contextData={currentAccount}
            title="Collection AI Assistant"
            maxHeight={400}
          />
        </Grid>
      </Grid>


      {/* Tabs for Dashboard/Sites */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="site view tabs"
          sx={{
            "& .MuiTab-root": {
              borderRadius: "8px 8px 0 0",
              minHeight: "48px",
              py: 1,
            },
            "& .MuiTabs-scrollButtons": {
              color: "primary.main",
            },
          }}
        >
          <Tab
            icon={<DashboardIcon />}
            iconPosition="start"
            label="Dashboard"
            id="site-tab-0"
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              "& .MuiSvgIcon-root": {
                mr: { xs: 0, sm: 1 },
                mb: { xs: 0.5, sm: 0 },
              },
            }}
          />
          <Tab
            icon={<ListIcon />}
            iconPosition="start"
            label="Sites"
            id="site-tab-1"
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              "& .MuiSvgIcon-root": {
                mr: { xs: 0, sm: 1 },
                mb: { xs: 0.5, sm: 0 },
              },
            }}
          />
        </Tabs>
      </Box>

      {/* Dashboard View */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Top Sites Chart */}
          <Grid item xs={12} md={6}>
            <StyledBarChart
              data={topSites.map((site, index) => ({
                name: site.siteName || "Unnamed Site",
                value: site.traffic,
                fill: COLORS[index % COLORS.length],
              }))}
              title="Top Performing Sites"
              subtitle="Monthly estimated traffic by site"
              dataKey="value"
              xAxisDataKey="name"
              height={300}
              isLoading={topSitesLoading}
              showEmptyState={topSites.length === 0}
            />
          </Grid>

          {/* Site Management Cards */}
          <Grid item xs={12} md={6}>
            <CardWithHeader
              noPadding={true}
              divider={false}
              title="Quick Actions"
              subtitle="Add a new site or import existing sites"
              headerIcon={<AddIcon color="primary" />}
              sx={{ height: "100%" }}
            >
              <Stack spacing={2} sx={{ p: 2, pt: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleCreate(true)}
                  fullWidth
                  sx={{ borderRadius: "8px" }}
                >
                  Add Site Link / Platform
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleCreate(false)}
                  fullWidth
                  sx={{ borderRadius: "8px" }}
                >
                  Create New Site
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ImportIcon />}
                  onClick={handleImportSites}
                  fullWidth
                  sx={{ borderRadius: "8px" }}
                >
                  Import Sites
                </Button>
              </Stack>
            </CardWithHeader>
          </Grid>

          {/* Site Stats Card */}
          {siteTypeSummary && siteTypeSummary.length > 0 && (
            <Grid item xs={12} md={6}>
              <CardWithHeader
                noPadding={true}
                divider={false}
                title="Site Type Summary"
                subtitle="Breakdown of sites by platform type"
                sx={{ height: "100%", p: 0 }}
              >
                <List sx={{ p: 0, pt: 2 }}>
                  {siteTypeSummary.map((entry, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        px: 3,
                        borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: COLORS[index % COLORS.length],
                            mr: 2,
                          }}
                        />
                        <Typography variant="body1">{entry.type}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Chip
                          label={entry.count}
                          sx={{
                            bgcolor: COLORS[index % COLORS.length] + "22",
                            color: COLORS[index % COLORS.length],
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </List>
              </CardWithHeader>
            </Grid>
          )}
        </Grid>
      )}

      {/* Collection Traffic Analytics - Full Width (shown when Dashboard tab is active) */}
      {tabValue === 0 && (
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12}>
            <AggregatedTrafficChart 
              level="account" 
              levelId={currentAccount.id} 
              title="Collection Traffic Analytics"
              height={300}
            />
          </Grid>
        </Grid>
      )}

      {/* Sites Tab */}
      {tabValue === 1 && (
        <>
          {/* Search and Filter */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Search sites"
                fullWidth
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    bgcolor: "#EAEEF3",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl
                sx={{
                  "& .MuiFormLabel-root": {
                    bgcolor: "#EAEEF3",
                  },
                }}
                fullWidth
              >
                <InputLabel id="siteType">Filter by type</InputLabel>
                <Select
                  labelId="siteType"
                  value={selectedSiteType}
                  onChange={(e) => setSelectedSiteType(e.target.value)}
                  input={<OutlinedInput label="Filter by type" />}
                  sx={{
                    borderRadius: "8px",
                  }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {siteTypeSummary.map((siteType) => (
                    <MenuItem key={siteType.type} value={siteType.type}>
                      {siteType.type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Site List with counts */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader
              title={
                <Badge
                  badgeContent={filteredSites.length}
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.75rem",
                      height: 20,
                      minWidth: 20,
                      right: -20,
                      top: 12,
                    },
                  }}
                >
                  <Box>Sites</Box>
                </Badge>
              }
            />
          </Box>

          {/* Site List Component */}
          {filteredSites.length > 0 ? (
            <SiteLists
              sitesList={{
                data: filteredSites,
                isLoading: sitesList.isLoading,
              }}
              onManage={(siteId, accountId) => {
                history(
                  `/account/${
                    accountId || currentAccount.id
                  }/site-details/${siteId}`
                );
              }}
            />
          ) : (
            <EmptyState
              icon={<SearchIcon sx={{ fontSize: 60 }} />}
              title="No sites found"
              description={
                search
                  ? "Try adjusting your search or filter criteria"
                  : "Create your first site to get started"
              }
              actionLabel={search ? "Clear filters" : "Create Site"}
              onAction={
                search
                  ? () => {
                      setSearch("");
                      setSelectedSiteType("");
                    }
                  : () => handleCreate(false)
              }
            />
          )}
        </>
      )}
    </Container>
  );
};

export default SiteList;
